import { lengthSq, sub } from '../math.ts';
import type { Rect } from './selection.ts';
import type { EditorTool, Vec2 } from './types.ts';

/** One corner of a selection frame, in the same {±1, ±1} space as the scale handles. */
export type GizmoCorner = { x: -1 | 1; y: -1 | 1 };

export const GIZMO_CORNERS: readonly GizmoCorner[] = [
  { x: -1, y: -1 },
  { x: 1, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
];

export type SelectionGizmoMode = 'none' | 'scale' | 'rotate' | 'both';

export type TransformGizmoHit =
  | { kind: 'handle'; corner: GizmoCorner }
  | { kind: 'rotate'; corner: GizmoCorner };

export function gizmoCornerPoint(frame: Rect, corner: GizmoCorner): Vec2 {
  return {
    x: corner.x < 0 ? frame.minX : frame.maxX,
    y: corner.y < 0 ? frame.minY : frame.maxY,
  };
}

export function gizmoOppositeCorner(corner: GizmoCorner): GizmoCorner {
  return { x: corner.x === -1 ? 1 : -1, y: corner.y === -1 ? 1 : -1 };
}

export function gizmoCornerId(corner: GizmoCorner): 'tl' | 'tr' | 'br' | 'bl' {
  if (corner.y < 0) {
    return corner.x < 0 ? 'tl' : 'tr';
  }

  return corner.x < 0 ? 'bl' : 'br';
}

/** Yellow rotate dots sit this far outside the matching corner, along the diagonal. */
export function gizmoRotateHandlePoint(frame: Rect, corner: GizmoCorner, offset: number): Vec2 {
  const at = gizmoCornerPoint(frame, corner);
  return {
    x: at.x + corner.x * offset,
    y: at.y + corner.y * offset,
  };
}

export function gizmoShowsScale(mode: SelectionGizmoMode): boolean {
  return mode === 'scale' || mode === 'both';
}

export function gizmoShowsRotate(mode: SelectionGizmoMode): boolean {
  return mode === 'rotate' || mode === 'both';
}

/** Vertex / bend / gizmo chrome. Off while View → Elevation is on. */
export function ringSculptingEnabled(showElevation: boolean): boolean {
  return !showElevation;
}

/**
 * Which corner dots a selection tool should draw. Hand and draw tools never
 * show the frame. Select + one object stays vertex-edit only. Elevation hides
 * the box — the plateau stroke is the selection read.
 */
export function selectionGizmoMode(
  tool: EditorTool,
  selectedCount: number,
  showElevation = false,
): SelectionGizmoMode {
  if (showElevation || selectedCount < 1) {
    return 'none';
  }

  const transformTool = tool === 'select' || tool === 'scale' || tool === 'rotate';

  if (selectedCount >= 2 && transformTool) {
    return 'both';
  }

  if (selectedCount === 1) {
    if (tool === 'scale') {
      return 'scale';
    }

    if (tool === 'rotate') {
      return 'rotate';
    }
  }

  return 'none';
}

/**
 * Hits the discrete dots the given mode draws. Scale sits on the frame corners;
 * rotate sits `rotateOffset` outside each corner. The nearer dot wins when both
 * are in range (they should not overlap at the authored offset).
 */
export function hitTransformGizmo(
  point: Vec2,
  frame: Rect,
  handleRadius: number,
  rotateOffset: number,
  mode: SelectionGizmoMode,
): TransformGizmoHit | null {
  if (mode === 'none') {
    return null;
  }

  let bestHandle: { corner: GizmoCorner; d2: number } | null = null;
  let bestRotate: { corner: GizmoCorner; d2: number } | null = null;
  const handleR2 = handleRadius * handleRadius;
  const showScale = gizmoShowsScale(mode);
  const showRotate = gizmoShowsRotate(mode);

  for (const corner of GIZMO_CORNERS) {
    if (showScale) {
      const d2 = lengthSq(sub(point, gizmoCornerPoint(frame, corner)));

      if (d2 <= handleR2 && (!bestHandle || d2 < bestHandle.d2)) {
        bestHandle = { corner, d2 };
      }
    }

    if (showRotate) {
      const d2 = lengthSq(sub(point, gizmoRotateHandlePoint(frame, corner, rotateOffset)));

      if (d2 <= handleR2 && (!bestRotate || d2 < bestRotate.d2)) {
        bestRotate = { corner, d2 };
      }
    }
  }

  if (bestHandle && bestRotate) {
    return bestHandle.d2 <= bestRotate.d2
      ? { kind: 'handle', corner: bestHandle.corner }
      : { kind: 'rotate', corner: bestRotate.corner };
  }

  if (bestHandle) {
    return { kind: 'handle', corner: bestHandle.corner };
  }

  if (bestRotate) {
    return { kind: 'rotate', corner: bestRotate.corner };
  }

  return null;
}
